/**
 * Color algorithms for word cloud
 * Ported from words_cloud_model.coffee calcFontFill method
 */

import * as d3 from 'd3'

/**
 * Convert number to 2-digit hex string
 * @param {number} number
 * @returns {string}
 */
function toHex(number) {
  const n = Math.round(number)
  if (n <= 0) return '00'
  if (n >= 255) return 'FF'
  const hex = n.toString(16)
  return hex.length === 1 ? '0' + hex : hex
}

/**
 * Create adaptive levels color function
 * Divides words into up to 7 levels based on size distribution
 *
 * @param {Object} sizes - Map of size -> count
 * @param {string[]} colors - Array of 7 colors
 * @returns {Function} Color function (size) => color
 */
export function createAdaptiveLevelsColorFn(sizes, colors) {
  if (!sizes || Object.keys(sizes).length === 0) {
    return () => colors[6]
  }

  let min = 99999
  let max = 0
  let levels = 0

  for (const s in sizes) {
    levels += 1
    const sizeNum = Number(s)
    min = Math.min(min, sizeNum)
    max = Math.max(max, sizeNum)
  }

  levels = Math.min(levels, 7)

  const ranges = []
  let currentMax = max

  for (let i = levels; i > 0; i--) {
    const diff = currentMax - min
    if (diff > i) {
      const part = Math.round(diff / i)
      ranges.push([currentMax - part, currentMax])
      currentMax = currentMax - part - 1

      // Find next valid max
      for (let j = currentMax; j >= 1; j--) {
        if (sizes[j] !== undefined) {
          currentMax = j
          break
        }
      }
    } else {
      ranges.push([currentMax - 1, currentMax])
      currentMax -= 1
    }
  }

  return size => {
    for (const i in ranges) {
      if (size >= ranges[i][0] && size <= ranges[i][1]) {
        return colors[i]
      }
    }
    return colors[6]
  }
}

/**
 * Create fixed levels color function using quantile scale
 *
 * @param {number} from - Min font size
 * @param {number} to - Max font size
 * @param {string[]} colors - Array of colors
 * @returns {Function} Color function (size) => color
 */
export function createLevelsColorFn(from, to, colors) {
  const diff = to - from
  const n = 0 === diff ? 1 : 6 <= diff ? 7 : diff + 1

  const fractions = []
  for (let i = 0; i < n; i++) {
    fractions.push(from + diff / (n * 2) + (diff / n) * i)
  }

  const scale = d3.scaleQuantile().domain(fractions).range(colors.slice(0, fractions.length).reverse())

  return size => scale(size)
}

/**
 * Create gradient color function
 * Interpolates between two colors based on size
 *
 * @param {number} from - Min font size
 * @param {number} to - Max font size
 * @param {string[]} colors - Array with at least 2 colors [start, ..., end]
 * @returns {Function} Color function (size) => color
 */
export function createGradientColorFn(from, to, colors) {
  const color1 = colors[colors.length - 1] // Start color (smallest words)
  const color2 = colors[0] // End color (largest words)

  const r1 = parseInt(color1.slice(1, 3), 16)
  const g1 = parseInt(color1.slice(3, 5), 16)
  const b1 = parseInt(color1.slice(5, 7), 16)

  const r2 = parseInt(color2.slice(1, 3), 16)
  const g2 = parseInt(color2.slice(3, 5), 16)
  const b2 = parseInt(color2.slice(5, 7), 16)

  const diff = to - from

  const rf = d3
    .scaleLinear()
    .domain([from, from + diff])
    .range([r1, r2])
  const gf = d3
    .scaleLinear()
    .domain([from, from + diff])
    .range([g1, g2])
  const bf = d3
    .scaleLinear()
    .domain([from, from + diff])
    .range([b1, b2])

  return size => `#${toHex(rf(size))}${toHex(gf(size))}${toHex(bf(size))}`
}

/**
 * Create circular/ordinal color function
 * Cycles through colors based on word index
 *
 * @param {string[]} colors - Array of colors to cycle through
 * @returns {Function} Color function (size, index) => color
 */
export function createCircleColorFn(colors) {
  const scale = d3.scaleOrdinal().range(colors)
  return (_size, index) => scale(index)
}

/**
 * Create color function based on style
 *
 * @param {string} style - Color style: 'adj_levels', 'levels', 'gradient', 'circle'
 * @param {Object} options - Configuration options
 * @param {string[]} options.colors - Color palette
 * @param {number} options.from - Min font size (realRangeFrom)
 * @param {number} options.to - Max font size (realRangeTo)
 * @param {Object} [options.sizes] - Size distribution map (for adj_levels)
 * @returns {Function} Color function
 */
export function createColorFunction(style, options) {
  const { colors, from, to, sizes } = options

  switch (style) {
    case 'adj_levels':
      if (sizes && Object.keys(sizes).length > 0) {
        return createAdaptiveLevelsColorFn(sizes, colors)
      }
      // Fall back to levels if no sizes
      return createLevelsColorFn(from, to, colors)

    case 'levels':
      return createLevelsColorFn(from, to, colors)

    case 'gradient':
      return createGradientColorFn(from, to, colors)

    case 'circle':
      return createCircleColorFn(colors)

    default:
      return createLevelsColorFn(from, to, colors)
  }
}

export default {
  createAdaptiveLevelsColorFn,
  createLevelsColorFn,
  createGradientColorFn,
  createCircleColorFn,
  createColorFunction
}
